import { Box, useTheme } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import {
  BoxForm,
  CodeField,
  NumberField,
  RadioGroup,
  Select,
  SelectAutocomplete,
  StackForm,
  Switch,
  TextField,
} from 'components/Form'
import { SectionHeader } from 'components/Header'
import Hint from 'components/Hint'
import { Link, TextLink } from 'components/Link'
import Paper from 'components/Paper'
import { useIsFieldDisabled } from 'features/globalSetup/forms/Prebid/Bidders/hooks/useIsFieldDisabled'
import { BidderSlice, CommonFormProps } from 'features/globalSetup/forms/Prebid/Bidders/types'
import { useGetPrioritiesForSelect } from 'features/globalSetup/hooks/useGetPrioritiesForSelect'
import { useGetYieldbirdStatusesForSelect } from 'features/globalSetup/hooks/useGetYieldbirdStatusesForSelect'
import { YieldbirdStatus } from 'features/globalSetup/types/yieldbirdStatus'
import { yieldbirdStatusColor } from 'features/globalSetup/utils/status'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { generateSelector } from 'providers/product-fruits/utils'
import { ROUTES } from 'routes'
import { BIDDER_CONFIG_LEARN_MORE, SELECT_HINT_LEARN_MORE } from './constants'
import { useAccountTypeOptions } from './hooks/useAccountTypeOptions'
import { CommonSchema } from './schemas/commonSchema'
import { EditSchema } from './schemas/editSchema'
import { useGetConsentModesForSelect } from './sections/hooks/useGetConsentModesForSelect'
import { useGetSupplyChainsForSelect } from './sections/hooks/useGetSupplyChainsForSelect'

export const CommonForm = ({ bidders, variant }: CommonFormProps) => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const theme = useTheme()
  const { isFieldDisabled } = useIsFieldDisabled()
  const { control } = useFormContext<CommonSchema>()

  const { t: tCommon } = useTranslation(['features/globalSetup', 'common'])
  const { t: tAdd } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.add',
  })
  const { t: tEdit } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.edit',
  })

  const { accountType, bidderId } = useWatch<CommonSchema>()
  const { bidderConfigEnabled } = useWatch<EditSchema>()
  const accountTypeOptions = useAccountTypeOptions()
  const statuses = useGetYieldbirdStatusesForSelect()
  const consentModes = useGetConsentModesForSelect()
  const priorities = useGetPrioritiesForSelect({ variant: 'workspaceBidder' })
  const supplyChains = useGetSupplyChainsForSelect()

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={tAdd('basicAttributes.header.subtitle')}
          title={tAdd('basicAttributes.header.title')}
        />

        <GapBox />

        <BoxForm>
          <RadioGroup
            control={control}
            data-product-fruits={generateSelector({ step: 2, subTour: 2, tour: 3 })}
            disabled={variant === 'edit'}
            label={tCommon('common.accountType.label')}
            name='accountType'
            options={accountTypeOptions}
            row
            tooltip={{
              content: [
                tCommon('common.accountType.tooltip.header', {
                  subject: tCommon('common.accountType.tooltip.subject.bidder'),
                }),
                tCommon('common.accountType.tooltip.paragraph1'),
                tCommon('common.accountType.tooltip.paragraph2'),
                tCommon('common.accountType.tooltip.paragraph3'),
              ],
            }}
          />

          {accountType === 'yieldbird' && variant === 'edit' && (
            <Select
              control={control}
              disabled={isFieldDisabled('status')}
              label={tCommon('common.status.label')}
              labelColor={({ value }: { value: YieldbirdStatus }) =>
                yieldbirdStatusColor({ status: value, theme })
              }
              name='status'
              options={statuses}
              tooltip={{
                content: tCommon('common.status.tooltip'),
              }}
            />
          )}

          {accountType === 'thirdPartyProvider' && (
            <Select
              control={control}
              disabled={variant === 'edit'}
              label={tCommon('common.supplyChain.label')}
              name='supplyChain'
              options={supplyChains}
              tooltip={{
                content: tCommon('common.supplyChain.tooltip.content'),
                link: {
                  title: tCommon('common.supplyChain.tooltip.link'),
                  url: generatePath(ROUTES.GLOBAL_SETUP.SCHAIN.LIST, { workspaceId }),
                },
              }}
            />
          )}

          <StackForm sx={{ alignItems: 'flex-start' }}>
            <Box>
              <SelectAutocomplete<CommonSchema, BidderSlice>
                control={control}
                data-product-fruits={generateSelector({ step: 3, subTour: 2, tour: 3 })}
                disabled={variant === 'edit' || isFieldDisabled('bidderId')}
                label={tAdd('basicAttributes.bidderId.label')}
                name='bidderId'
                options={bidders}
              />

              <Hint
                content={tAdd('basicAttributes.bidderId.hint')}
                contentEndAdornment={
                  <Link
                    newTab
                    to={SELECT_HINT_LEARN_MORE}
                  >
                    {tCommon('common:learnMore')}
                  </Link>
                }
              />
            </Box>

            <TextField
              control={control}
              disabled={isFieldDisabled('bidderCode')}
              label={tAdd('basicAttributes.bidderCode.label')}
              name='bidderCode'
              placeholder={tAdd('basicAttributes.bidderCode.label')}
              tooltip={{
                content: tAdd('basicAttributes.bidderCode.tooltip'),
              }}
            />
          </StackForm>

          <StackForm data-product-fruits={generateSelector({ step: 4, subTour: 2, tour: 3 })}>
            <Box>
              <TextField
                control={control}
                disabled={isFieldDisabled('bidderSid')}
                label={tAdd('basicAttributes.bidderSid.label')}
                name='bidderSid'
                placeholder={tAdd('basicAttributes.bidderSid.label')}
                tooltip={{
                  content: tAdd('basicAttributes.bidderSid.tooltip'),
                }}
              />

              {bidderId?.value !== undefined && bidderId.rawData?.sidRequired === false && (
                <Hint
                  content={tAdd('basicAttributes.bidderSid.hint', { bidderName: bidderId.label })}
                />
              )}
            </Box>

            <NumberField
              control={control}
              disabled={isFieldDisabled('bidCpmAdjustment')}
              label={tAdd('basicAttributes.bidCpmAdjustment.label')}
              name='bidCpmAdjustment'
              tooltip={{
                content: tAdd('basicAttributes.bidCpmAdjustment.tooltip'),
              }}
            />

            <Select
              control={control}
              disabled={isFieldDisabled('priority')}
              label={tAdd('basicAttributes.priority.label')}
              name='priority'
              options={priorities}
              tooltip={{
                content: tAdd('basicAttributes.priority.tooltip'),
              }}
            />

            <Select
              control={control}
              disabled={isFieldDisabled('consentMode')}
              label={tAdd('basicAttributes.consentMode.label')}
              name='consentMode'
              options={consentModes}
              tooltip={{
                content: tAdd('basicAttributes.consentMode.tooltip'),
              }}
            />
          </StackForm>
        </BoxForm>
      </Paper>

      {variant === 'edit' &&
        ability.can('read', 'GlobalSetupField-prebid-bidders-general-bidderConfig') && (
          <Paper>
            <SectionHeader
              subtitle={tEdit('general.bidderConfig.header.subtitle')}
              subtitleEndAdornment={
                <TextLink
                  link={{
                    title: tCommon('common:learnMore'),
                    url: BIDDER_CONFIG_LEARN_MORE,
                  }}
                />
              }
              title={tEdit('general.bidderConfig.header.title')}
              titleEndAdornment={
                <Switch
                  control={control}
                  disabled={isFieldDisabled('bidderConfig')}
                  name='bidderConfigEnabled'
                />
              }
            />

            {bidderConfigEnabled && (
              <BoxForm>
                <GapBox />

                <CodeField
                  control={control}
                  disabled={isFieldDisabled('bidderConfig')}
                  language='json'
                  name='bidderConfig'
                  title={tEdit('general.bidderConfig.field.title')}
                />
              </BoxForm>
            )}
          </Paper>
        )}
    </>
  )
}
